// Connexion « avec mon NIP » (service Identité & NIP, OpenID Connect) : bouton en premier choix sur la page de
// connexion quand le service est configuré, et bloc de liaison, de préremplissage et de déliaison dans « Mon
// identité ». Le NIP n'est jamais saisi ici : il l'est chez le service, avec le mot de passe et le code.
import type { EtatConnexionNip, EtatNipUsager, PreremplissageNip } from '@cc/contrats';
import { Bandeau, Bouton } from '@cc/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { construireChemin, requete } from '../../../api/client';
import { messageErreur } from '../../../api/erreurs';
import styles from '../citoyen.module.css';

/** Messages de retour de l'émetteur (paramètre nip), jamais de détail technique. */
const RETOURS: Record<string, { type: 'info' | 'attention' | 'erreur' | 'succes'; texte: string }> = {
  non_lie: { type: 'info', texte: 'Votre NIP n\'est pas encore lié à un compte CollectivitésConnect : connectez-vous avec votre identifiant, puis liez votre NIP depuis « Mon identité ».' },
  echec: { type: 'erreur', texte: 'La connexion par NIP n\'a pas abouti. Utilisez votre identifiant habituel ou réessayez.' },
  annule: { type: 'info', texte: 'Connexion par NIP annulée.' },
  expire: { type: 'attention', texte: 'La demande de connexion par NIP a expiré : recommencez.' },
  session_expiree: { type: 'attention', texte: 'La liaison du NIP n\'a pas été faite : votre session était fermée. Connectez-vous, puis liez votre NIP depuis « Mon identité ».' },
  indisponible: { type: 'attention', texte: 'La connexion par NIP n\'est pas possible pour ce compte : utilisez votre identifiant habituel.' },
  lie: { type: 'succes', texte: 'Votre NIP est lié : vous pouvez désormais vous connecter avec lui.' },
  deja_lie: { type: 'erreur', texte: 'Ce NIP est déjà lié à un autre compte. Adressez-vous au guichet de votre collectivité.' },
  autre_nip: { type: 'erreur', texte: 'Un autre NIP est déjà lié à ce compte : déliez-le d\'abord.' },
};

export function RetourNip() {
  const [params] = useSearchParams();
  const r = RETOURS[params.get('nip') ?? ''];
  return r ? <Bandeau type={r.type}>{r.texte}</Bandeau> : null;
}

async function demarrer(chemin: string, authentifie: boolean): Promise<void> {
  const r = authentifie
    ? await requete<{ url: string }>(chemin, { methode: 'POST' })
    : await fetch(construireChemin(chemin), { method: 'POST', credentials: 'same-origin' }).then(async (x) => {
        if (!x.ok) throw new Error('Le service de connexion par NIP est indisponible : utilisez votre identifiant habituel.');
        return (await x.json()) as { url: string };
      });
  window.location.assign(r.url);
}

/** Premier choix de la page de connexion ; rien n'est affiché si le service n'est pas configuré. */
export function BoutonConnexionNip() {
  const etat = useQuery({
    queryKey: ['connexion-nip', 'etat'],
    queryFn: () => fetch(construireChemin('/public/connexion-nip/etat'), { credentials: 'same-origin' }).then((r) => (r.ok ? (r.json() as Promise<EtatConnexionNip>) : { active: false })),
    staleTime: 300_000,
  });
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  if (!etat.data?.active) return null;
  return (
    <section aria-labelledby="connexion-nip-titre" className={styles.pile}>
      <h2 id="connexion-nip-titre" className={styles.titreSection}>
        Se connecter avec mon NIP
      </h2>
      <p className={styles.aide}>Votre NIP, votre mot de passe et votre code vous sont demandés sur la page sécurisée du service d&apos;identité.</p>
      {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
      <Bouton
        pleineLargeur
        chargement={enCours}
        libelleChargement="Redirection en cours"
        onClick={() => {
          setEnCours(true);
          demarrer('/public/connexion-nip/debut', false).catch((e: unknown) => {
            setErreur(messageErreur(e));
            setEnCours(false);
          });
        }}
      >
        Se connecter avec mon NIP
      </Bouton>
      <p className={styles.aide}>Pas de NIP, ou service indisponible : connectez-vous avec votre téléphone ou votre courriel ci-dessous.</p>
    </section>
  );
}

const LIBELLES: Record<string, string> = { nom: 'Nom', prenoms: 'Prénoms', date_naissance: 'Date de naissance', lieu_naissance: 'Lieu de naissance', sexe: 'Sexe' };

/** Bloc « Mon NIP » de la page identité ; onPreremplir reçoit les attributs vérifiés proposés. */
export function BlocNipUsager({ onPreremplir }: { onPreremplir: (p: PreremplissageNip) => void }) {
  const client = useQueryClient();
  const etat = useQuery({ queryKey: ['usager', 'identite-nip'], queryFn: () => requete<EtatNipUsager>('/usager/identite-nip') });
  const [enCours, setEnCours] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [propose, setPropose] = useState<PreremplissageNip | null>(null);
  const e = etat.data;
  if (!e || (!e.connexion_disponible && !e.lie)) return null;
  const agir = async (cle: string, f: () => Promise<void>) => {
    setErreur(null);
    setEnCours(cle);
    try {
      await f();
    } catch (x) {
      setErreur(messageErreur(x));
    } finally {
      setEnCours(null);
    }
  };
  return (
    <section className={styles.carte} aria-labelledby="mon-nip-titre">
      <h2 id="mon-nip-titre" className={styles.blocTitre}>
        Mon NIP
      </h2>
      <RetourNip />
      {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
      {e.lie ? (
        <>
          <p>Votre NIP est lié à ce compte. CollectivitésConnect ne conserve pas votre NIP, seulement un identifiant propre à ce site.</p>
          {e.preremplissage_disponible ? (
            <Bouton variante="secondaire" chargement={enCours === 'pre'} onClick={() => void agir('pre', async () => setPropose((await requete<{ preremplissage: PreremplissageNip }>('/usager/identite-nip/preremplissage')).preremplissage))}>
              Préremplir avec mes informations vérifiées
            </Bouton>
          ) : null}
          {propose ? (
            Object.keys(propose).length === 0 ? (
              <Bandeau type="info">Aucune information vérifiée n&apos;est disponible pour le moment.</Bandeau>
            ) : (
              <div className={styles.pile}>
                <ul>
                  {Object.entries(propose).map(([k, v]) => (
                    <li key={k}>
                      {LIBELLES[k] ?? k} : {v}
                    </li>
                  ))}
                </ul>
                <p className={styles.aide}>Rien n&apos;est enregistré tant que vous ne validez pas le formulaire ci-dessous. Cela ne vaut pas vérification au guichet.</p>
                <Bouton variante="secondaire" onClick={() => { onPreremplir(propose); setPropose(null); }}>
                  Reporter dans le formulaire
                </Bouton>
              </div>
            )
          ) : null}
          <Bouton variante="discret" chargement={enCours === 'delier'} onClick={() => void agir('delier', async () => { await requete('/usager/identite-nip/liaison', { methode: 'DELETE' }); await client.invalidateQueries({ queryKey: ['usager', 'identite-nip'] }); })}>
            Ne plus me connecter avec mon NIP
          </Bouton>
        </>
      ) : (
        <>
          <p>Liez votre NIP pour vous connecter avec lui. Votre NIP, votre mot de passe et votre code vous sont demandés sur la page du service d&apos;identité.</p>
          <Bouton variante="secondaire" chargement={enCours === 'lier'} onClick={() => void agir('lier', () => demarrer('/usager/identite-nip/liaison', true))}>
            Lier mon NIP
          </Bouton>
        </>
      )}
    </section>
  );
}
